import styled from '@emotion/styled'
import AttachMoney from '@mui/icons-material/AttachMoney'
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp'
import PercentIcon from '@mui/icons-material/Percent'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import SearchIcon from '@mui/icons-material/Search'
import { FormControl, type FormControlProps, TextField, type TextFieldProps } from '@mui/material'
import type { DivProps } from 'commons/types/htmlElements'
import { shouldForwardProp } from 'commons/utils/mui'
import { theme as newTheme } from 'styles/theme/hav-theme'

import { baseInputStyles } from '../styles'
import { DSInputType } from './index'

type TextFieldStyledProps = TextFieldProps & {
  design: DSInputType
  height?: number
  error?: boolean
  hasWarning?: boolean
  addorment?: number
  hasmultiline?: number
  textuppercase?: boolean
  successoutline?: boolean
  showspinbuttons?: boolean
}

interface WrapLabelStyledProps extends DivProps {
  hasInfo?: boolean
  disabled?: boolean
}

interface FormControlStyledProps extends FormControlProps {
  width?: number
}

export const TextFieldStyled = styled(TextField, {
  shouldForwardProp,
})<TextFieldStyledProps>`
  .MuiInputLabel-root {
    display: none;
  }
  .MuiOutlinedInput-root {
    height: ${({ height }) => (height ? height + 'px' : 'unset')};
    background-color: ${newTheme.colors.neutral.white};
    font-size: ${newTheme.typography.sizes.xs};
    ${({ hasmultiline }) =>
      hasmultiline &&
      `
        padding: 0px;
        padding-right: ${newTheme.spacing.md};
      `}
    input[disabled] {
      background-color: ${newTheme.colors.neutral[100]};
      -webkit-text-fill-color: ${newTheme.colors.neutral.mediumhover};
    }
    textarea[disabled] {
      background-color: ${newTheme.colors.neutral[100]};
      -webkit-text-fill-color: ${newTheme.colors.neutral.mediumhover};
    }

    textarea::-webkit-scrollbar {
      border-radius: 0;
      width: 25px;
      height: ${newTheme.spacing.sm};
    }

    textarea::-webkit-scrollbar-thumb {
      border: 8px solid rgba(0, 0, 0, 0);
      background-clip: padding-box;
      border-radius: 9999px;
      background-color: ${newTheme.colors.neutral.light};
    }

    textarea::-webkit-scrollbar-track {
      border-radius: ${newTheme.shapes.borderRadius.md};
    }
  }

  .MuiInputBase-root {
    overflow: hidden;
    ${baseInputStyles};
    ${({ error }) =>
      error &&
      `
        border: ${newTheme.shapes.borderWidth.thin} ${newTheme.colors.alert.danger[30]} !important;
      `}

    ${({ hasWarning }) =>
      hasWarning &&
      `
        border: ${newTheme.shapes.borderWidth.thin} ${newTheme.colors.alert.warning[40]} !important;
      `}

    ${({ successoutline }) =>
      successoutline &&
      `
        border: ${newTheme.shapes.borderWidth.thin} ${newTheme.colors.support.success} !important;
      `}
  }

  .MuiInputBase-input {
    ${({ textuppercase }) => textuppercase && `text-transform: uppercase;`}
    ${({ disabled }) => disabled && `cursor: not-allowed;`}

    ::placeholder {
      text-transform: none;
    }

    font-family: ${newTheme.typography.family.primary};

    height: ${({ height }) => (height ? `${height - 20}px` : 'unset')};

    &[value='0'] {
      color: ${newTheme.colors.neutral.light};
    }

    ${({ addorment }) => addorment && `padding-right: 0;`}

    ${({ design }) =>
      design === DSInputType.TEXT_INPUT &&
      `
        font-size: ${newTheme.typography.sizes.xs};
        font-family: ${newTheme.typography.family.primary};
        font-weight: ${newTheme.typography.weight.lg};
        line-height: normal
      `}

    ${({ error }) =>
      error &&
      `
      background-color: ${newTheme.colors.alert.danger[10]} !important;
    `}


    ${({ hasWarning }) =>
      hasWarning &&
      `
      background-color: ${newTheme.colors.alert.warning[10]} !important;
    `}
  }

  fieldset {
    font-size: 0;

    ${({ disabled }) =>
      disabled &&
      `

      `}

    legend {
      display: none;
    }
  }

  .Mui-disabled {
    .MuiInputAdornment-root {
      svg {
        path {
          fill: ${newTheme.colors.neutral.medium};
        }
      }
    }
  }

  label[data-shrink='false'] + .MuiInputBase-formControl input.MuiInputBase-input {
    ::placeholder {
      opacity: 1 !important;
      color: ${newTheme.colors.neutral.light};
      font-size: ${newTheme.typography.sizes.xs};
      font-family: ${newTheme.typography.family.primary};
    }
  }

  ${({ design, error }) =>
    design === DSInputType.MASTER_INPUT &&
    (
      error
        ? `border-color: ${newTheme.colors.alert.danger[40]};`
        : `border-color: ${newTheme.colors.neutral.white};`
    )}

  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`

export const SearchIconStyled = styled(SearchIcon)`
  fill: ${newTheme.colors.primary.main};
  font-size: ${newTheme.icon.size.medium};
`
export const PercentStyled = styled(PercentIcon)`
  fill: ${newTheme.colors.primary.main};
  font-size: ${newTheme.icon.size.medium};
`

export const MoneyStyled = styled(AttachMoney)`
  fill: ${newTheme.colors.primary.main};
  font-size: ${newTheme.icon.size.medium};
`

export const ArrowUpStyled = styled(KeyboardArrowUp)`
  fill: ${newTheme.colors.primary.main};
  font-size: 12px;
  cursor: pointer;
`

export const ArrowDownStyled = styled(KeyboardArrowDown)`
  fill: ${newTheme.colors.primary.main};
  font-size: 12px;
  cursor: pointer;
`

export const SpinButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-left: ${newTheme.spacing.xs};
  height: 18px;
`

export const ErrorIconStyled = styled(ReportProblemOutlinedIcon)`
  fill: ${newTheme.colors.alert.danger.main};
  font-size: ${newTheme.icon.size.small};
`

export const WrapLabelStyled = styled('div', { shouldForwardProp })<WrapLabelStyledProps>`
  display: flex;
  gap: 3px;
  align-items: center;
  white-space: nowrap;

  ${({ disabled }) => disabled && `cursor: not-allowed;`}
  #LabelInputForm {
    align-self: end;
    font-size: ${newTheme.typography.sizes['2xs']};
    ${({ hasInfo }) => hasInfo && `margin-right: ${newTheme.spacing.xs};`}

    .MuiInputLabel-asterisk {
      color: ${newTheme.colors.secondary};
    }
  }
`

export const FormControlStyled = styled(FormControl)<FormControlStyledProps>`
  ${({ width }) => width && `width: ${width}px;`}
  ${({ disabled }) => disabled && `cursor: not-allowed;`}
`

export const LabelAdornmentStyled = styled.div`
  display: flex;
  align-items: center;
  font-size: ${newTheme.typography.sizes['2xs']};
  font-weight: ${newTheme.typography.weight.xsm};
`
export const MaxLengthStyled = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-grow: 1;
  font-size: ${newTheme.typography.sizes['2xs']};
  font-weight: ${newTheme.typography.weight.md};
  color: ${newTheme.colors.neutral.high};
  line-height: ${newTheme.typography.sizes.xs};

  ${({ position }: { position: string }) =>
    position === 'bottom' && `margin-top: ${newTheme.spacing.xs};`}
`

export const LabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`
